<script setup>
import { onMounted, useTemplateRef } from 'vue';
import { ButtonTypes } from '@@/components/Common/Button.vue';
import { scrollActiveElementIntoView } from '@@/utils/Components/ComponentUtils';

const props = defineProps({
  canShowSort: {
    type: Boolean,
    default: false,
  },
  containerClass: {
    type: String,
    default: null,
  },
  navItems: {
    type: Array,
    default: null,
  },
  selectedNavItem: {
    type: Number,
    default: 0,
  },
  showNavItems: {
    type: Boolean,
    default: true,
  },
  sort: {
    type: String,
    default: null,
  },
  sortOptions: {
    type: Array,
    default: null,
  },
});

const $emit = defineEmits(['nav-item-click', 'sort-change']);

const buttonContainer = useTemplateRef('buttonContainer');
const buttons = useTemplateRef('buttons');

const mappedSortOptions = computed(() => {
  if (!Array.isArray(props.sortOptions)) {
    return null;
  }

  return props.sortOptions.map((sortOption) => {
    if (typeof sortOption === 'string') {
      return { key: sortOption, value: sortOption };
    }

    const { field, isAllAccess, label } = sortOption;

    return {
      isAllAccess,
      key: label,
      value: field,
    };
  });
});

const getButtonProps = (navItem, index) => ({
  active: index === props.selectedNavItem,
  canShowChipActiveIcon: false,
  additionalClassNames: 'tw-mr-1.5 last:tw-mr-0',
  to: navItem.to,
  type: ButtonTypes.chipCard,
});

const handleNavItemClick = ($event, navItem) => $emit('nav-item-click', $event, navItem);
const handleSortChange = (value) => $emit('sort-change', value);

onMounted(() => {
  const activeElement = buttons.value?.[props.selectedNavItem]?.$el;
  const options = { block: 'nearest', inline: 'center' };
  scrollActiveElementIntoView({
    parent: buttonContainer.value,
    activeElement,
    options,
  });
});
</script>

<template>
  <div :class="containerClass">
    <div
      v-if="showNavItems"
      ref="buttonContainer"
      class="tw-overflow-x-auto tw-whitespace-nowrap hide-scrollbar-on-mobile"
    >
      <Button
        v-for="(navItem, index) in navItems"
        :key="index"
        ref="buttons"
        v-bind="getButtonProps(navItem, index)"
        @click="($event) => handleNavItemClick($event, navItem)"
      >
        {{ navItem.text }}
        <font-awesome-icon
          v-if="navItem.isLocked"
          :class="[
            index === selectedNavItem ? 'tw-text-white' : 'all-access-color',
            'tw-text-sm tw-pl-0.5',
          ]"
          icon="lock"
        />
      </Button>
    </div>
    <div
      v-if="canShowSort"
      class="tw-flex tw-items-center tw-mt-1.5"
    >
      <div class="tw-overflow-x-auto hide-scrollbar-on-mobile">
        <CompareSort
          container-class="tw-pl-1.5"
          :options="mappedSortOptions"
          :model-value="sort"
          @update:model-value="handleSortChange"
        />
      </div>
    </div>
  </div>
</template>
